import { FormField } from "../../types/form-types";

/**
 * normalizeKey - Normalizes a string for comparison (lowercase + underscores)
 * Used for mapping URL parameters to field labels
 * @param text - The string to normalize
 * @returns Normalized string
 */
export const normalizeKey = (text: string): string => {
  if (!text) return "";
  return text
    .trim()
    .toLowerCase()
    .replace(/[\s\-.]+/g, "_") // Replace spaces, hyphens, and dots with underscores
    .replace(/[^a-z0-9_]/g, ""); // Remove any other special characters
};

/**
 * mapInitialValuesToFields - Maps external key-value pairs to internal field IDs
 * Follows Priority Mapping (AC-003):
 * 1. Match field.dataKey
 * 2. Match normalizeKey(field.label)
 * 3. Match field.id (UUID)
 * 
 * @param initialValues - External data (e.g., from URL)
 * @param fields - Form structure fields
 * @returns Map of <fieldId, value>
 */
export const mapInitialValuesToFields = (
  initialValues: Record<string, any>,
  fields: any[]
): Map<string, any> => {
  const mapped = new Map<string, any>();
  if (!initialValues || !fields) return mapped;

  Object.entries(initialValues).forEach(([incomingKey, value]) => {
    const normalizedIncoming = normalizeKey(incomingKey);

    // Find the first field that matches any of the criteria
    const targetField = fields.find((field) => {
      // Priority 1: dataKey (Human-defined stable key)
      if (field.dataKey && field.dataKey === incomingKey) return true;
      
      // Priority 2: Label (Human-readable match)
      if (field.label && normalizeKey(field.label) === normalizedIncoming) return true;
      
      // Priority 3: ID (Internal UUID match)
      if (field.id === incomingKey) return true;

      return false;
    });

    if (targetField) {
      mapped.set(targetField.id, value);
    }
  });

  return mapped;
};

/**
 * formAnswerUtils.ts
 *
 * Utilities for handling form answers extraction, parsing, and conversion.
 * Handles multiple answer formats including Python dict strings and metadata wrappers.
 *
 * Key Functions:
 * - getAnswersFromFormAnswers() - Extract answers from various wrapper formats
 * - extractAnswerValue() - Parse string/object answers, handle Python dict strings
 * - mapAnswersToRecord() - Convert Map to Record format
 * - mergeAnswerMaps() - Merge multiple answer maps with priority handling
 */

/**
 * Extract answers object from formAnswers prop (handles both formats)
 * @param formAnswersObj - Answers in either direct format or wrapped format
 * @returns Extracted answers record
 */
export const getAnswersFromFormAnswers = (formAnswersObj: any): Record<string, any> => {
  if (!formAnswersObj) return {};
  // Handle both formats: direct answers { fieldId: value } or wrapped { answers: { fieldId: value } }
  return formAnswersObj.answers || formAnswersObj;
};

/**
 * Extract actual value from answer object
 * Handles string representation of Python dicts, JSON strings, and metadata-wrapped objects
 * @param answerObj - Answer value that may be string, object, or primitive
 * @returns Extracted/parsed answer value
 */
export const extractAnswerValue = (answerObj: any): any => {
  // Handle string representation of Python dict or JSON
  if (typeof answerObj === 'string') {
    // Only try to parse if it looks like JSON/Python dict (starts with { or [)
    const trimmed = answerObj.trim();
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        // Try to parse as JSON first (in case it's valid JSON)
        try {
          const parsed = JSON.parse(answerObj);
          if (parsed && typeof parsed === 'object' && 'value' in parsed) {
            return parsed.value;
          }
          return parsed;
        } catch {
          // If JSON parse fails, try to parse as Python dict string
          // Replace Python-style booleans and None with JSON equivalents
          const pythonToJson = answerObj
            .replace(/: True/g, ': true')
            .replace(/: False/g, ': false')
            .replace(/: None/g, ': null')
            .replace(/'/g, '"'); // Replace single quotes with double quotes

          const parsed = JSON.parse(pythonToJson);
          if (parsed && typeof parsed === 'object' && 'value' in parsed) {
            return parsed.value;
          }
          return parsed;
        }
      } catch (e) {
        // Only log warning if it looked like it should have been parseable
        console.warn('[extractAnswerValue] Failed to parse JSON/Python dict:', answerObj, e);
        return answerObj; // Return as-is if parsing fails
      }
    }

    // Plain string value (not JSON/Python dict) - return as-is
    return answerObj;
  }

  // Handle object with metadata
  if (typeof answerObj === 'object' && answerObj !== null && 'value' in answerObj) {
    return answerObj.value;
  }

  return answerObj;
};

/**
 * Convert a Map of answers to a Record format
 * @param answersMap - Map<fieldId, value>
 * @returns Record<fieldId, value>
 */
export const mapAnswersToRecord = (answersMap: Map<string, any>): Record<string, any> => {
  const record: Record<string, any> = {};
  answersMap.forEach((value, key) => {
    record[key] = value;
  });
  return record;
};

/**
 * Convert answers Record to Map format
 * @param answersRecord - Record<fieldId, value>
 * @returns Map<fieldId, value>
 */
export const recordAnswersToMap = (answersRecord: Record<string, any>): Map<string, any> => {
  return new Map(Object.entries(answersRecord));
};

/**
 * Merge multiple answer maps with priority handling
 * Later maps in the array take precedence over earlier ones
 * @param maps - Array of answer maps to merge
 * @returns Merged map with combined answers
 */
export const mergeAnswerMaps = (...maps: Map<string, any>[]): Map<string, any> => {
  const merged = new Map<string, any>();

  for (const map of maps) {
    map.forEach((value, key) => {
      merged.set(key, value);
    });
  }

  return merged;
};

/**
 * Apply extracted answer values to answers map
 * Used when loading answers from formAnswers prop that may have metadata wrappers
 * @param answersRecord - Source answers with potential metadata
 * @returns Clean answers map with extracted values
 */
export const cleanAnswerValues = (answersRecord: Record<string, any>): Map<string, any> => {
  const cleaned = new Map<string, any>();

  Object.entries(answersRecord).forEach(([fieldId, value]) => {
    cleaned.set(fieldId, extractAnswerValue(value));
  });

  return cleaned;
};
